import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { Notification } from '../../../core/models/core.models';
import { SocketService } from '@services/socket.service';
import { ToastRef } from 'ngx-toastr';
import { patchNotificationProgress } from 'src/app/api/queries/notification.queries';
import { logInfoWithTime } from 'src/app/shared/utils/date-utils';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass],
})
export class NotificationComponent {
  @Input() notification: Notification;
  @Input() toastRef: ToastRef<any>;
  notificationHtml: SafeHtml;

  private readonly queryClient = inject(QueryClient);

  constructor(
    private sanitizer: DomSanitizer,
    private socketService: SocketService,
  ) {}

  /**
   * Predefined variable renderers.
   * Each key is the variable name expected in the template,
   * and its value is a function that returns an HTML string for that variable.
   */
  variableRenderers: { [key: string]: (value: any) => string } = {
    user: (value: any) =>
      `<img src="${value.perma_image_url}" alt="${value.text}" class="avatar"/> ${value.text}`,
    object: (value: any) =>
      `<a href="/object/${value.object_id}">${value.text}</a>`,
    algorithm: (value: any) => `${value.text}`,
    feed: (value: any) => `<a href="">${value.text}</a>`,
    period: (value: any) => `${value.text}`,
    post: (value: any) => `<a href="">${value.text}</a>`,

    // You can add additional renderers for other variables (e.g., algorithm, comment, etc.)
  };

  ngOnChanges() {
    //this.updateNotificationHtml();
  }

  /**
   * Updates the sanitized HTML message for the notification.
   */
  private updateNotificationHtml(): void {
    if (this.notification) {
      const message = this.getDynamicNotificationMessage(this.notification);
      this.notificationHtml = this.sanitizer.bypassSecurityTrustHtml(message);
    }
  }

  ngOnInit(): void {
    // Build the dynamic message and sanitize the resulting HTML.
    this.updateNotificationHtml();
    logInfoWithTime(
      `NotificationComponent -> ngOnInit: New notification from the server with id=[${this.notification?.fingerprint_id}]`,
    );
    if (this.notification?.has_progress && !this.notification?.is_finished) {
      const notificationId = this.notification.fingerprint_id;

      // Subscribed on the shared user socket, so tracking outlives this toast and keeps
      // updating the store until the task reports it is finished.
      this.socketService.subscribeTaskProgress(notificationId, (data) => {
        if (data.progress) {
          patchNotificationProgress(this.queryClient, notificationId, {
            overallProgress: data.progress.overall_progress,
            overallProgressMax: data.progress.overall_progress_max,
            overallProgressPercent: data.progress.overall_progress_percent,
            isFinished: data.progress.is_finished,
          });

          if (data.progress.is_finished) {
            this.socketService.unsubscribeTaskProgress(notificationId);
          }
        }
      });
    }

    if (this.toastRef) {
      this.toastRef.afterActivate().subscribe(() => {
        // 🔹 Automatically close the toast after 5 seconds
        setTimeout(() => {
          this.toastRef.close();
        }, 5000);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.toastRef) {
      this.toastRef.close(); // Close the toast when the component is destroyed
    }
    // Progress tracking is intentionally NOT torn down here: it lives on the user socket
    // and must keep running after this toast is dismissed. It ends on is_finished.
  }

  closeToast() {
    if (this.closeToast) {
      this.toastRef.close(); // Close the toast manually
    }
  }

  /**
   * Dynamically replaces template variables in the notification message
   * with rendered values based on predefined variable renderers.
   *
   * The method scans for placeholders of the format {{variable}}
   * and for each match, if the payload contains that key, it uses the corresponding
   * renderer if defined; otherwise, it falls back to a default representation.
   *
   * @param notification The notification object from the API.
   * @returns The notification message with all placeholders replaced by dynamic HTML.
   */
  getDynamicNotificationMessage(notification: Notification): string {
    let messageTemplate: string = notification.notification_text_template || '';
    if (notification.has_progress && notification.is_finished) {
      messageTemplate = notification.notification_text_finished;
    }
    const payload = notification.payload || {};

    // Use a regular expression to detect all variables in the template.
    return messageTemplate.replace(
      /{{\s*(.*?)\s*}}/g,
      (match, variableName) => {
        if (payload.hasOwnProperty(variableName)) {
          const value = payload[variableName];
          // If a custom renderer exists for this variable, use it.
          if (this.variableRenderers.hasOwnProperty(variableName)) {
            return this.variableRenderers[variableName](value);
          } else {
            // Default rendering: if value is an object with a "title" property, return the title.
            if (typeof value === 'object' && value !== null && value?.text) {
              return value.text;
            }
            // Otherwise, convert the value to a string.
            return String(value);
          }
        }
        // If the variable is not present in the payload, replace with an empty string.
        return '';
      },
    );
  }

  get notificationSeverity(): string {
    const severityId = this.notification?.notification_severity_id;

    if (!severityId) return '';

    switch (severityId) {
      case 1:
        return 'info';
      case 2:
        return 'warning';
      case 3:
        return 'error';
      default:
        return '';
    }
  }
}
